import React from 'react';
import PropTypes from 'prop-types';
import { MessageCircle, Mail, Phone, Check } from 'lucide-react';
import { cn } from 'src/lib/utils';

// "Waiting On" cell for the First Contact tab, matching the reference
// screenshot's two variants: a candidate is either waiting on a first
// reply (three quick-contact icons — chat/email/call) or on their details
// form (a row of pending-field chips, with a green check on whatever's
// already done).
//
// NOTE: none of `waiting_on_type` / `waiting_on_items` are confirmed API
// fields — there's no real payload for the "default"/"secondary" flows'
// per-candidate detail yet, so this is a best-effort visual match built to
// fail gracefully (falls back to the "first reply" look) rather than a
// verified data contract. Update field names once the real shape lands.
// The three contact icons are intentionally not wired to anything (no
// quick-contact action has been designed) — same treatment as the row's
// kebab menu.
const IconButton = ({ icon: Icon }) => (
  <span className="flex items-center justify-center w-6 h-6 rounded-md border border-border text-muted-foreground">
    <Icon className="h-3.5 w-3.5" />
  </span>
);

IconButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  icon: PropTypes.elementType.isRequired,
};

const WaitingOnCell = ({ row }) => {
  if (row.waiting_on_type === 'details_form') {
    const items = row.waiting_on_items || [];
    return (
      <div>
        <p className="text-sm font-semibold text-foreground-strong mb-1.5">
          Their details form
        </p>
        <div className="flex flex-wrap gap-1.5">
          {items.map((item) => (
            <span
              key={item.label}
              className={cn(
                'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full',
                item.done
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700'
              )}
            >
              {item.done && <Check className="h-3 w-3" />}
              {item.label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm font-semibold text-foreground-strong mb-1.5">
        A first reply
      </p>
      <div className="flex items-center gap-1.5">
        <IconButton icon={MessageCircle} />
        <IconButton icon={Mail} />
        <IconButton icon={Phone} />
      </div>
    </div>
  );
};

WaitingOnCell.propTypes = {
  row: PropTypes.shape({
    waiting_on_type: PropTypes.string,
    waiting_on_items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        done: PropTypes.bool,
      })
    ),
  }).isRequired,
};

export const waitingOnColumn = {
  key: 'waiting_on',
  header: 'Waiting On',
  className: 'text-sm',
  render: (_v, row) => <WaitingOnCell row={row} />,
};

export default WaitingOnCell;
